import { push } from "@lagunovsky/redux-react-router";
import { size, split } from "lodash";
import { stopSubmit } from "redux-form";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { Column, Container, Row, Separator, TextDisplay } from "src/styled";
import { logOut, validateCode } from "src/redux/reducers";
import { RightDisplayPanel } from "src/components";
import { translate } from "src/services";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import RubiconLogo from "src/assets/svg/RubiconLogoWhite.svg";
import ValidateCodeForm, {
  ValidateCodeFormValues,
  VALIDATE_CODE_FORM_NAME,
} from "./ValidateCodeForm";

const ValidateCodePage = () => {
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();

  const codeFromUrl = Number(search?.split("?code=")?.pop()?.split("&")[0]);
  const emailFromUrl = search?.split("&email=")[1];
  const phoneFromUrl = search?.split("&phone=")[1];
  const vendorFromUrl = split(pathname, "/")[1];

  const {
    vendorURL,
    user: { email, phone },
  } = useAppSelector((state) => state.auth);

  const destinationMessage = useMemo(
    () => translate(`common.${!!size(email || emailFromUrl) ? "email" : "phone"}`),
    [email, emailFromUrl]
  );

  const handleValidateCode = useCallback(
    (values: ValidateCodeFormValues) => {
      (vendorFromUrl || vendorURL) &&
        (phone || phoneFromUrl || email || emailFromUrl) &&
        validateCode({
          code: values.code || codeFromUrl,
          phone: phone || phoneFromUrl,
          email: email || emailFromUrl,
          vendorUrl: vendorFromUrl || vendorURL,
        })(dispatch)
          .then(() => {
            dispatch(push(`/${vendorFromUrl || vendorURL}/request`));
          })
          .catch((e) => {
            const { data } = e.response;
            const errorCode = data ? data.code : null;
            let errorMessage = "";
            if (errorCode === "CustomerBulkySchedulerInvalidCode")
              errorMessage = translate("alertMessages.codeDoesNotMatch");

            if (errorMessage) dispatch(stopSubmit(VALIDATE_CODE_FORM_NAME, { code: errorMessage }));
          });
    },
    [dispatch, email, phone, vendorURL, codeFromUrl, emailFromUrl, phoneFromUrl, vendorFromUrl]
  );

  useEffect(() => {
    if (codeFromUrl && vendorFromUrl && (phoneFromUrl || emailFromUrl)) {
      dispatch(logOut());

      handleValidateCode({
        phone: phoneFromUrl,
        email: emailFromUrl,
        vendorURL: vendorFromUrl,
        code: codeFromUrl,
      });
    }
  }, [codeFromUrl, emailFromUrl, phoneFromUrl, vendorFromUrl, handleValidateCode, dispatch]);

  return (
    <>
      <Container fluid>
        <Row>
          <Column lg={6} padding="no medium">
            <Separator height={120} />
            <TextDisplay
              size="xLarge"
              block
              maxWidth={520}
              align="center"
              weight="medium"
              margin="large auto no auto"
              desktopMargin="xxLarge auto no auto"
            >
              {translate("loginPage.enterOneTimeCode", {
                destination: destinationMessage,
              })}{" "}
            </TextDisplay>

            <Separator height={120} />
            <ValidateCodeForm
              onSubmit={handleValidateCode}
              emailFromUrl={emailFromUrl}
              phoneFromUrl={phoneFromUrl}
              vendorFromUrl={vendorFromUrl}
            />
            <Separator height={120} />
          </Column>
        </Row>
      </Container>
      <RightDisplayPanel backgroundColor="primary" topLogo={RubiconLogo}>
        <TextDisplay size="large" color="gray" margin="sMedium no no" desktopMargin="medium no no">
          {translate("bannerContent.adaCompliance")}
        </TextDisplay>
        <TextDisplay size="large" color="gray" margin="sMedium no no" desktopMargin="medium no no">
          {translate("bannerContent.contactIfIssues")}
        </TextDisplay>
        <TextDisplay size="large" color="gray" margin="sMedium no no" desktopMargin="medium no no">
          {translate("bannerContent.thankYouNote")}
        </TextDisplay>
      </RightDisplayPanel>
    </>
  );
};

export default ValidateCodePage;
